/**
 * Player API — Inventory, feeds, wallet balance and theme.
 *
 * These endpoints are called from the player's browser (your custom UI) while a
 * player is in a game. They cover the player's inventory of revealed cards, the
 * public "just-pulled" activity feed, the player's live wallet balance, and the
 * resolved theme to render the experience with.
 *
 * Most endpoints require the player session token, sent in the
 * `X-Platform-Session` request header (the PlatformSession scheme). The
 * just-pulled feed is the exception: it is public and needs no token, so it can
 * power a live activity ticker even before a player signs in.
 *
 * Each exported value is an OpenAPI 3.1 path item.
 */
import { TEMPLATE_NAMES } from '@game-platform/theme-constructor';

export const feedsPaths: Record<string, unknown> = {
  '/loot_machine/api/game_platform/iframe/v1/inventory': {
    get: {
      tags: ['Inventory & Feeds'],
      summary: 'List the player inventory',
      description: [
        "Returns the player's inventory: the cards they have already revealed, newest first.",
        '',
        'Each card includes its title, artwork, value, the tier badge it earned, and a',
        'live buyback offer (`best_active_offer`) showing how much the player would receive',
        'if they sell the card back right now. Use `pool_order_id` on a card to sell it back',
        'through the buyback endpoint.',
        '',
        'The result is paginated; `meta.total_count` is the total number of revealed cards the',
        'player owns across all pages. Requires the player session token.',
      ].join('\n'),
      operationId: 'listPlayerInventory',
      security: [{ PlatformSession: [] }],
      parameters: [
        {
          name: 'page',
          in: 'query',
          required: false,
          schema: { type: 'integer', minimum: 1, default: 1 },
          description: '1-based page number. Defaults to 1; values below 1 are treated as 1.',
        },
        {
          name: 'per',
          in: 'query',
          required: false,
          schema: { type: 'integer', minimum: 1, maximum: 100, default: 32 },
          description: 'Number of cards per page. Defaults to 32, capped at 100.',
        },
        { $ref: '#/components/parameters/IncludeCardDetails' },
      ],
      responses: {
        '200': {
          description: "A page of the player's revealed cards.",
          content: {
            'application/json': {
              schema: {
                type: 'object',
                title: 'PlayerInventory',
                required: ['pool_items', 'meta'],
                properties: {
                  pool_items: {
                    type: 'array',
                    description: 'The cards the player has revealed, newest first.',
                    items: {
                      type: 'object',
                      title: 'InventoryCard',
                      required: ['id', 'title', 'item_price', 'pool_order_id'],
                      properties: {
                        id: { type: 'integer', description: 'Unique card id.' },
                        title: { type: 'string', description: 'Card title. May be an empty string when unnamed.' },
                        image_url: {
                          type: 'string',
                          format: 'uri',
                          nullable: true,
                          description: 'Card artwork URL. Null when the card has no image.',
                        },
                        item_price: { type: 'number', description: 'The card value.' },
                        opened_at: {
                          type: 'string',
                          format: 'date-time',
                          nullable: true,
                          description: 'When the card was revealed (ISO 8601). Always set for inventory cards.',
                        },
                        fair_market_value: {
                          type: 'number',
                          nullable: true,
                          description: 'Estimated market value of the card. Null when unknown.',
                        },
                        badge: {
                          type: 'object',
                          nullable: true,
                          description: 'The rarity tier badge earned by the card. Null when the card has no badge.',
                          required: ['id', 'name'],
                          properties: {
                            id: { type: 'integer', description: 'Badge id.' },
                            name: { type: 'string', description: 'Badge name (the rarity tier, e.g. "Epic").' },
                          },
                        },
                        best_active_offer: {
                          type: 'object',
                          nullable: true,
                          description:
                            'The current buyback offer for this card — what the player gets if they sell it back now. Null when no buyback is available.',
                          required: ['id', 'gross_price', 'net_price', 'created_at'],
                          properties: {
                            id: { type: 'integer', description: 'Buyback offer id.' },
                            gross_price: { type: 'number', description: 'Amount credited to the player on sell-back.' },
                            net_price: { type: 'number', description: 'Amount after the marketplace fee.' },
                            buyback_rate: {
                              type: 'number',
                              nullable: true,
                              description: 'Buyback rate applied to the offer. Null when not set.',
                            },
                            auto_sell_at: {
                              type: 'string',
                              format: 'date-time',
                              nullable: true,
                              description: 'When the card auto-sells if untouched (ISO 8601). Null when not scheduled.',
                            },
                            created_at: {
                              type: 'string',
                              format: 'date-time',
                              description: 'When this buyback offer was created (ISO 8601).',
                            },
                          },
                        },
                        status: {
                          type: 'string',
                          nullable: true,
                          enum: [
                            'on_hands',
                            'listed',
                            'redeemed',
                            'buyback',
                            'burned',
                            'reserved',
                            'unavailable',
                          ],
                          description: 'Current state of the card in the inventory. Null on older cards.',
                        },
                        pool_order_id: {
                          type: 'integer',
                          description: 'The order (purchase round) this card came from. Pass it to the buyback endpoint to sell the card back.',
                        },
                        fairness_ticket_id: {
                          type: 'string',
                          description:
                            'Present only on cards from a provably-fair purchase (and only while provably fair is on for your platform): ' +
                            'the ticket id of that purchase. Load its receipt with **Get a receipt** to offer Verify.',
                        },
                        sold_from_pool_offer: {
                          type: 'object',
                          nullable: true,
                          description: 'The pack this card was revealed from. Null when unavailable.',
                          required: ['id', 'title'],
                          properties: {
                            id: { type: 'integer', description: 'Game id of the source pack.' },
                            title: { type: 'string', description: 'Title of the source pack — reflects the pack rename set in the console, when one is configured. May be an empty string.' },
                            image_url: {
                              type: 'string',
                              format: 'uri',
                              nullable: true,
                              description: 'Artwork URL of the source pack. Null when unavailable.',
                            },
                          },
                        },
                        card_details: { $ref: '#/components/schemas/CardDetails' },
                      },
                    },
                  },
                  meta: { $ref: '#/components/schemas/PaginationMeta' },
                },
              },
              example: {
                pool_items: [
                  {
                    id: 90211,
                    title: '2023 Panini Prizm LeBron James #1',
                    image_url: 'https://cdn.getcardbase.com/blobs/abc123/front.png',
                    item_price: 49.99,
                    opened_at: '2026-05-28T14:03:11Z',
                    fair_market_value: 120.0,
                    badge: { id: 3, name: 'Epic' },
                    best_active_offer: {
                      id: 55012,
                      gross_price: 110.0,
                      net_price: 99.0,
                      buyback_rate: 0.85,
                      auto_sell_at: null,
                      created_at: '2026-05-28T14:03:12.000Z',
                    },
                    status: 'on_hands',
                    pool_order_id: 7781,
                    sold_from_pool_offer: {
                      id: 463,
                      title: 'HOF ELITE MMA Pack',
                      image_url: 'https://cdn.getcardbase.com/packs/463/pack.png',
                    },
                  },
                ],
                meta: { total_count: 1, page: 1, per: 32 },
              },
            },
          },
        },
        '401': {
          description: 'Missing, invalid or expired player session token.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '404': {
          description: 'Requested resource not found.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '422': {
          description: 'The request could not be processed.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '500': {
          description: 'Unexpected server error. The message carries a correlation id for support.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
      },
    },
  },

  '/loot_machine/api/game_platform/iframe/v1/pool_items/just_pulled': {
    get: {
      tags: ['Inventory & Feeds'],
      summary: 'List recent reveals (just-pulled feed)',
      description: [
        'Returns the most recently revealed cards across all players, newest first — the',
        'live activity feed you can show as a "just pulled" ticker.',
        '',
        'This endpoint is **public**: no player session token is required, so it can run on a',
        'landing page or before a player signs in. Pass `pool_offer_id` to limit the feed to a',
        'single game (pack), and `min_fair_market_value` to only include high-value pulls.',
        '',
        'Each entry is a lightweight card with its title, artwork, tier badge, and the source',
        'pack it came from. The result is paginated; `meta.total_count` reflects the total number',
        'of matching reveals (respecting the `pool_offer_id` and `min_fair_market_value` filters',
        'when set).',
      ].join('\n'),
      operationId: 'listJustPulledFeed',
      security: [],
      parameters: [
        {
          name: 'pool_offer_id',
          in: 'query',
          required: false,
          schema: { type: 'integer' },
          description:
            'Optional game (pack) id. When set, the feed is limited to reveals from that pack. When omitted, the feed spans all games.',
        },
        {
          name: 'min_fair_market_value',
          in: 'query',
          required: false,
          schema: { type: 'number' },
          description:
            'Optional minimum fair market value, in USD (e.g. `100` for $100). When set, only pulls with a fair market value at or above this amount are returned; cards without a known fair market value are excluded. Combines with `pool_offer_id`.',
        },
        {
          name: 'page',
          in: 'query',
          required: false,
          schema: { type: 'integer', minimum: 1, default: 1 },
          description: '1-based page number. Defaults to 1; values below 1 are treated as 1.',
        },
        {
          name: 'per',
          in: 'query',
          required: false,
          schema: { type: 'integer', minimum: 1, maximum: 100, default: 32 },
          description: 'Number of cards per page. Defaults to 32, capped at 100.',
        },
        { $ref: '#/components/parameters/IncludeCardDetails' },
      ],
      responses: {
        '200': {
          description: 'A page of recently revealed cards.',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                title: 'JustPulledFeed',
                required: ['pool_items', 'meta'],
                properties: {
                  pool_items: {
                    type: 'array',
                    description: 'Recently revealed cards across all players, newest first.',
                    items: {
                      type: 'object',
                      title: 'JustPulledCard',
                      required: ['id', 'title'],
                      properties: {
                        id: { type: 'integer', description: 'Unique card id.' },
                        title: { type: 'string', description: 'Card title. May be an empty string when unnamed.' },
                        image_url: {
                          type: 'string',
                          format: 'uri',
                          nullable: true,
                          description: 'Card artwork URL. Null when the card has no image.',
                        },
                        opened_at: {
                          type: 'string',
                          format: 'date-time',
                          nullable: true,
                          description: 'When the card was revealed (ISO 8601). Always set for feed entries.',
                        },
                        fair_market_value: {
                          type: 'number',
                          nullable: true,
                          description: 'Estimated fair market value of the card, in USD. Null when no estimate is available.',
                        },
                        badge: {
                          type: 'object',
                          nullable: true,
                          description: 'The rarity tier badge earned by the card. Null when the card has no badge.',
                          required: ['id', 'name'],
                          properties: {
                            id: { type: 'integer', description: 'Badge id.' },
                            name: { type: 'string', description: 'Badge name (the rarity tier, e.g. "Legendary").' },
                          },
                        },
                        pool_offer: {
                          type: 'object',
                          nullable: true,
                          description: 'The game (pack) this card was revealed from. Null when unavailable.',
                          required: ['id', 'title'],
                          properties: {
                            id: { type: 'integer', description: 'Game id of the source pack.' },
                            title: { type: 'string', description: 'Title of the source pack — reflects the pack rename set in the console, when one is configured. May be an empty string.' },
                            image_url: {
                              type: 'string',
                              format: 'uri',
                              nullable: true,
                              description: 'Artwork URL of the source pack. Null when unavailable.',
                            },
                          },
                        },
                        card_details: { $ref: '#/components/schemas/CardDetails' },
                      },
                    },
                  },
                  meta: { $ref: '#/components/schemas/PaginationMeta' },
                },
              },
              example: {
                pool_items: [
                  {
                    id: 90233,
                    title: '2023 Topps Chrome Shohei Ohtani #200',
                    image_url: 'https://cdn.getcardbase.com/blobs/def456/front.png',
                    opened_at: '2026-05-29T09:15:42Z',
                    fair_market_value: 125.5,
                    badge: { id: 5, name: 'Legendary' },
                    pool_offer: {
                      id: 463,
                      title: 'HOF ELITE MMA Pack',
                      image_url: 'https://cdn.getcardbase.com/packs/463/pack.png',
                    },
                  },
                ],
                meta: { total_count: 1, page: 1, per: 32 },
              },
            },
          },
        },
        '500': {
          description: 'Unexpected server error. The message carries a correlation id for support.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
      },
    },
  },

  '/loot_machine/api/game_platform/iframe/v1/balance': {
    get: {
      tags: ['Player Misc'],
      summary: 'Get the player wallet balance',
      description: [
        "Returns the player's current wallet balance, in minor units (cents), along with its",
        'currency. The balance is fetched live from your platform, so it always reflects the',
        "player's up-to-date funds.",
        '',
        'If your platform cannot be reached or returns an unusable response, this endpoint',
        'responds with **502 Bad Gateway** and a `BALANCE_FETCH_FAILED` error.',
        '',
        'Requires the player session token.',
      ].join('\n'),
      operationId: 'getPlayerWalletBalance',
      security: [{ PlatformSession: [] }],
      responses: {
        '200': {
          description: "The player's live wallet balance.",
          content: {
            'application/json': {
              schema: {
                type: 'object',
                title: 'WalletBalance',
                required: ['result'],
                properties: {
                  result: {
                    type: 'object',
                    required: ['balance_cents', 'currency'],
                    properties: {
                      balance_cents: {
                        type: 'integer',
                        description: 'Wallet balance in minor units (cents), e.g. 9500 for $95.00.',
                      },
                      currency: {
                        type: 'string',
                        description: 'ISO currency code for the balance, e.g. "USD".',
                      },
                    },
                  },
                },
              },
              example: {
                result: {
                  balance_cents: 9500,
                  currency: 'USD',
                },
              },
            },
          },
        },
        '401': {
          description: 'Missing, invalid or expired player session token.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '502': {
          description:
            'Could not retrieve the balance from your platform (timeout or unusable response). Returns a `BALANCE_FETCH_FAILED` error.',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                title: 'BalanceFetchFailed',
                required: ['error', 'error_detail'],
                properties: {
                  error: { type: 'integer', enum: [1], description: 'Always 1 — marks a failure response.' },
                  error_detail: {
                    type: 'object',
                    required: ['code', 'message'],
                    properties: {
                      code: {
                        type: 'string',
                        enum: ['BALANCE_FETCH_FAILED'],
                        description: 'Always "BALANCE_FETCH_FAILED".',
                      },
                      message: {
                        type: 'string',
                        description: 'Human-readable detail about why the balance could not be fetched.',
                      },
                    },
                  },
                },
              },
              example: {
                error: 1,
                error_detail: {
                  code: 'BALANCE_FETCH_FAILED',
                  message: 'The balance request to your platform returned an unusable response',
                },
              },
            },
          },
        },
      },
    },
  },

  '/loot_machine/api/game_platform/iframe/v1/design_system': {
    get: {
      tags: ['Player Misc'],
      summary: 'Get the resolved theme',
      description: [
        'Returns the template and theme to render the player experience with. The result is',
        "resolved from the player's session, so it already reflects your platform's branding.",
        '',
        '`template` is the layout to render (`classic`, `modern`, `sleek` or `glass`). `design_system` carries the',
        "theme tokens for that template: the version, a color palette, and — when your platform",
        'has supplied them — optional `overrides`, `typography`, `radius` and `shadow` blocks.',
        'Only the keys you have customized appear; the rest fall back to the template defaults.',
        '',
        'Requires the player session token.',
      ].join('\n'),
      operationId: 'getPlayerTheme',
      security: [{ PlatformSession: [] }],
      responses: {
        '200': {
          description: 'The resolved template and theme tokens.',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                title: 'PlayerTheme',
                required: ['template', 'design_system'],
                properties: {
                  template: {
                    type: 'string',
                    enum: [...TEMPLATE_NAMES],
                    description: 'The layout to render for the player.',
                  },
                  design_system: {
                    type: 'object',
                    description: 'Theme tokens for the template, with your platform overrides applied on top of the defaults.',
                    required: ['version', 'palette'],
                    properties: {
                      version: { type: 'integer', enum: [2], description: 'Theme schema version. Always 2.' },
                      palette: {
                        type: 'object',
                        description: 'Color tokens for the theme (all hex strings).',
                        required: [
                          'primary',
                          'textOnBrand',
                          'accent',
                          'surfaceBorder',
                          'heroFrom',
                          'heroTo',
                          'textPrimary',
                        ],
                        properties: {
                          primary: { type: 'string', description: 'Primary brand color (hex).' },
                          textOnBrand: { type: 'string', description: 'Text color on the brand color (hex).' },
                          accent: { type: 'string', description: 'Accent color (hex).' },
                          surfaceBorder: { type: 'string', description: 'Surface border color (hex).' },
                          heroFrom: { type: 'string', description: 'Hero gradient start color (hex).' },
                          heroTo: { type: 'string', description: 'Hero gradient end color (hex).' },
                          textPrimary: { type: 'string', description: 'Primary text color (hex).' },
                        },
                      },
                      overrides: {
                        type: 'object',
                        nullable: true,
                        additionalProperties: true,
                        description: 'Per-component theme overrides. Present only when your platform supplies them.',
                      },
                      typography: {
                        type: 'object',
                        nullable: true,
                        additionalProperties: true,
                        description: 'Typography tokens. Present only when your platform supplies them.',
                      },
                      radius: {
                        type: 'object',
                        nullable: true,
                        additionalProperties: true,
                        description: 'Corner-radius tokens. Present only when your platform supplies them.',
                      },
                      shadow: {
                        type: 'object',
                        nullable: true,
                        additionalProperties: true,
                        description: 'Shadow tokens. Present only when your platform supplies them.',
                      },
                    },
                  },
                  lobby_url: {
                    type: 'string',
                    nullable: true,
                    description:
                      'The `lobby_url` your platform passed when creating the session, echoed back so a ' +
                      'custom UI can offer "Back to lobby". `null` when none was passed.',
                  },
                  deposit_url: {
                    type: 'string',
                    nullable: true,
                    description:
                      'The `deposit_url` your platform passed when creating the session — the hosted ' +
                      'experience offers it to the player on an insufficient balance. `null` when none was passed.',
                  },
                  provably_fair: {
                    type: 'boolean',
                    enum: [true],
                    description:
                      'Present (and `true`) only while provably fair is on for your platform and applies to this player: ' +
                      'buy with a ticket (see **Provably Fair**) and offer Verify on results. Absent otherwise.',
                  },
                },
              },
              example: {
                template: 'modern',
                lobby_url: 'https://example.com/lobby',
                deposit_url: 'https://example.com/cashier',
                design_system: {
                  version: 2,
                  palette: {
                    primary: '#FF4D4D',
                    textOnBrand: '#FFFFFF',
                    accent: '#FF4D4D',
                    surfaceBorder: '#262626',
                    heroFrom: '#1A0B0B',
                    heroTo: '#000000',
                    textPrimary: '#FFFFFF',
                  },
                },
              },
            },
          },
        },
        '401': {
          description: 'Missing, invalid or expired player session token.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
        '500': {
          description: 'Unexpected server error. The message carries a correlation id for support.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
      },
    },
  },
};
